// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
// KPI-/Meilenstein-/Trend-Logik, portiert aus docs/referenz/trainingslog.html
import type { WorkoutEntry, PlanDayItem } from "./types"
import { volume } from "./sets"
import { dayOf, daysAgo } from "./dates"

export const MILESTONES: Array<[number, string]> = [
  [1, "Erste Einheit – der Anfang ist gemacht"],
  [5, "5 Einheiten – die Routine wächst"],
  [10, "10 Einheiten – der Rumpf wird spürbar stabiler"],
  [25, "25 Einheiten – echte Trainingsgewohnheit"],
  [50, "50 Einheiten – stark, stabil, beständig"],
]

/** Einzigartige Trainingstage ("Einheiten"), sortiert aufsteigend. */
export function uniqueDates(entries: WorkoutEntry[]): string[] {
  return [...new Set(entries.map((e) => dayOf(e.date)))].sort()
}

export function totalVolume(entries: WorkoutEntry[]): number {
  return entries.reduce((s, e) => s + volume(e.sets ?? []), 0)
}

export function unitsLast7Days(entries: WorkoutEntry[], now: number = Date.now()): number {
  return uniqueDates(entries.filter((e) => daysAgo(dayOf(e.date), now) <= 6)).length
}

/** Überschreitet das Hinzufügen von `newDate` einen Meilenstein? → dessen Text, sonst null. */
export function milestoneCrossed(entriesBefore: WorkoutEntry[], newDate: string): string | null {
  const before = uniqueDates(entriesBefore).length
  const after = new Set([...uniqueDates(entriesBefore), newDate]).size
  if (after === before) return null
  const hit = MILESTONES.find(([n]) => n === after)
  return hit ? hit[1] : null
}

/** Sind alle Übungen des Tages (Items) für `date` geloggt? (nach diesem Eintrag) */
export function isDayComplete(items: PlanDayItem[], entries: WorkoutEntry[], date: string): boolean {
  if (!items.length) return false
  const logged = new Set(entries.filter((e) => dayOf(e.date) === date).map((e) => e.exercise))
  return items.every((it) => logged.has(it.exercise))
}

/** Unicode-Sparkline: max. Gewicht je Einheit, letzte 8 Einheiten dieser Übung. */
export function sparkline(entriesOfExercise: WorkoutEntry[]): string {
  const perDay = entriesOfExercise
    .slice()
    .sort((a, b) => dayOf(a.date).localeCompare(dayOf(b.date)))
    .map((e) => Math.max(...(e.sets ?? []).map((s) => s.weight_kg || 0), 0))
    .slice(-8)
  if (!perDay.length) return ""
  const lo = Math.min(...perDay)
  const hi = Math.max(...perDay)
  const bars = "▁▂▃▄▅▆▇█"
  return perDay.map((v) => bars[hi === lo ? 4 : Math.round(((v - lo) / (hi - lo)) * 7)]).join("")
}

/** ISO-Kalenderwoche "KW 32" + Buckets: Volumen je Woche, letzte n Wochen. */
export function isoWeek(iso: string): { year: number; week: number } {
  const d = new Date(iso + "T12:00")
  const target = new Date(d.valueOf())
  const dayNr = (d.getDay() + 6) % 7
  target.setDate(target.getDate() - dayNr + 3)
  const firstThursday = new Date(target.getFullYear(), 0, 4)
  const diff = target.valueOf() - firstThursday.valueOf()
  const week = 1 + Math.round((diff / 864e5 - 3 + ((firstThursday.getDay() + 6) % 7)) / 7)
  return { year: target.getFullYear(), week }
}

export interface WeekBucket { label: string; volume: number }

/** Volumen je ISO-Woche über die letzten `weeks` Wochen (inkl. leerer Wochen). */
export function weeklyVolume(entries: WorkoutEntry[], weeks = 12, now: number = Date.now()): WeekBucket[] {
  const byWeek = new Map<string, number>()
  for (const e of entries) {
    const { year, week } = isoWeek(dayOf(e.date))
    const key = `${year}-${week}`
    byWeek.set(key, (byWeek.get(key) ?? 0) + volume(e.sets ?? []))
  }
  const out: WeekBucket[] = []
  const cursor = new Date(now)
  cursor.setDate(cursor.getDate() - (weeks - 1) * 7)
  for (let i = 0; i < weeks; i++) {
    const { year, week } = isoWeek(cursor.toLocaleDateString("sv-SE"))
    out.push({ label: `KW ${week}`, volume: byWeek.get(`${year}-${week}`) ?? 0 })
    cursor.setDate(cursor.getDate() + 7)
  }
  return out
}

/** Volumen je Körperpartie (Datenset-Vokabular als Schlüssel), absteigend sortiert. */
export function volumeByBodyPart(
  entries: WorkoutEntry[],
  bodyPartByExercise: Map<string, string>,
): Array<{ bodyPart: string; volume: number }> {
  const acc = new Map<string, number>()
  for (const e of entries) {
    const bp = bodyPartByExercise.get(e.exercise) || "sonstiges"
    acc.set(bp, (acc.get(bp) ?? 0) + volume(e.sets ?? []))
  }
  return [...acc.entries()]
    .map(([bodyPart, vol]) => ({ bodyPart, volume: vol }))
    .filter((x) => x.volume > 0)
    .sort((a, b) => b.volume - a.volume)
}

/** Motivationstext wie im Prototyp. */
export function motivText(units: number, last7: number): string {
  if (units === 0) return "Los geht's – jeder starke Rücken beginnt mit Einheit 1."
  if (units >= 25) return "Beeindruckend – dein Rücken wird von Woche zu Woche belastbarer."
  if (last7 >= 2) return "Stark! Genau diese Regelmäßigkeit baut Stabilität und Knochendichte auf."
  return "Zeit für die nächste Einheit – dein Rücken dankt dir jede einzelne."
}
